import { useQuery } from '@tanstack/react-query'

import { endpoints } from './endpoints'
import { keys } from './keys'
import type { RentalCapabilityDto, RentalErrorDto, RentalHostOfferDto, RentalOffersDto } from './types'
import { isQuoteLive, type RentalPath } from '../core/rental'

/**
 * The console's view of a provider's rental API.
 *
 * Two reads and one derivation. The reads are ordinary queries; the
 * derivation — `unavailablePaths` — is where the capability answer becomes the
 * thing the path chooser actually needs, and it is a pure function so it can
 * be tested without a provider.
 */

export interface RentalQueryState<T> {
  data: T | undefined
  isLoading: boolean
  isFetching: boolean
  error: unknown
  refetch: () => Promise<T | undefined>
}

/**
 * A provider's capability, or undefined while unknown.
 *
 * Short stale time: a provider that stops serving rental (draining a cluster,
 * an incident) must stop being offered promptly, and a tenant holding a stale
 * "enabled" answer is walked into a request that cannot succeed.
 */
export function useRentalCapability(provider: string | undefined): RentalQueryState<RentalCapabilityDto> {
  const query = useQuery({
    queryKey: keys.rentalCapability(provider ?? ''),
    queryFn: ({ signal }) => endpoints.rentalCapability(provider ?? '', { signal }),
    enabled: Boolean(provider),
    staleTime: 30_000,
    // A provider that does not serve rental answers 404 or 502 forever;
    // retrying hammers it and delays the disabled state the tenant needs to
    // see. One retry covers a transient blip.
    retry: 1,
  })
  return { data: query.data, isLoading: query.isLoading, isFetching: query.isFetching, error: query.error, refetch: async () => (await query.refetch()).data }
}

/**
 * A provider's rentable hosts.
 *
 * Shorter stale time than the catalog, because a host offer is a snapshot of
 * free capacity and the whole product depends on it being recent. Plan §4.2
 * gives each offer its own expiry height; `freshOffers` below applies it.
 */
export function useRentalOffers(provider: string | undefined): RentalQueryState<RentalOffersDto> {
  const query = useQuery({
    queryKey: keys.rentalOffers(provider ?? ''),
    queryFn: ({ signal }) => endpoints.rentalOffers(provider ?? '', { signal }),
    enabled: Boolean(provider),
    staleTime: 15_000,
    refetchInterval: 30_000,
    retry: 1,
  })
  return { data: query.data, isLoading: query.isLoading, isFetching: query.isFetching, error: query.error, refetch: async () => (await query.refetch()).data }
}

/**
 * The offers still usable at this height.
 *
 * An offer past its expiry height must not be shown as rentable: requesting a
 * quote against it fails, and Gate 6 scenario 9 requires a stale host to be
 * excluded rather than merely marked. Filtered here rather than in the
 * component so every consumer gets the same answer.
 *
 * A height of 0 or undefined means the console does not know the chain height
 * yet. Everything is filtered OUT in that case rather than shown: offering a
 * host whose freshness cannot be checked is exactly the claim plan §8 forbids.
 */
export function freshOffers(
  offers: RentalHostOfferDto[] | undefined,
  height: number | undefined,
): RentalHostOfferDto[] {
  if (!offers || !height) return []
  return offers.filter((offer) => height < offer.expiry_height)
}

/** Offers that can serve one product. */
export function offersForMode(offers: RentalHostOfferDto[], mode: string): RentalHostOfferDto[] {
  return offers.filter((offer) => offer.products.includes(mode))
}

/**
 * The price per block this offer will be quoted at for one product, or null.
 *
 * `product_prices` wins when it names the mode. Only the shared-capacity
 * products may fall back to `price_per_block`: that is the host's base price,
 * and it is what those products were priced at before per-product prices
 * existed. An EXCLUSIVE host never falls back. Its quote is the whole host,
 * which a provider prices well above the base, so a ceiling built from
 * `price_per_block` is a guaranteed refusal — null lets the screen say the
 * true thing instead: this provider publishes no exclusive price.
 */
export function offerPriceForMode(offer: RentalHostOfferDto | undefined, mode: string): string | null {
  if (!offer) return null
  const posted = offer.product_prices?.[mode]
  if (posted) return posted
  if (mode === 'RENTAL_MODE_DIRECT_HOST_AVAILABLE' || mode === 'RENTAL_MODE_DIRECT_CUSTOM') {
    return offer.price_per_block || null
  }
  return null
}

/**
 * Why a provider shows no rentable host, for a tenant.
 *
 * Two different facts produce the same empty offer list. A provider holding a
 * quote lock suppresses the locked host from its offers — and when the lock is
 * the tenant's own pending quote, "nothing to rent" is false: the host frees
 * when that quote expires. Only with no locks held is the host truly empty,
 * and with no capability answer at all the console knows neither.
 */
export function emptyOffersCopy(capability: RentalCapabilityDto | undefined): string {
  if (!capability) {
    // Not "nothing to rent" — the console has not been able to ask. Saying the
    // host is empty here is the same false claim this function exists to stop.
    return 'This host could not be checked for available machines right now. Try again in a moment, or choose another host.'
  }
  if (capability.held_locks > 0) {
    return `This host is reserved by a pending quote. It frees within ${capability.max_quote_lifetime_blocks} block${capability.max_quote_lifetime_blocks === 1 ? '' : 's'}, when that quote expires.`
  }
  return 'This host has nothing to rent right now. Try another host, or set a maximum price and request offers.'
}

/**
 * Which rental paths this provider cannot serve, with the tenant-facing
 * reason.
 *
 * This is capability-aware routing (Phase 4 step 6). The rule it encodes: a
 * direct action is offered only on a POSITIVE answer. Unknown, still loading,
 * errored, disabled, and "no host can serve this product" all disable it —
 * because in every one of those states the only possible outcome of the button
 * is a failed request, and a tenant told a host was rentable who then gets an
 * error has been misled by the console rather than by the provider.
 *
 * Competitive is never disabled. It needs no provider capability at all: the
 * request goes to the whole network through the chain, so a provider that
 * cannot quote can still bid.
 */
export function unavailablePaths(
  capability: RentalCapabilityDto | undefined,
  isLoading: boolean,
  error: unknown,
  freshOfferList: RentalHostOfferDto[],
): Partial<Record<RentalPath, string>> {
  const out: Partial<Record<RentalPath, string>> = {}

  const directPaths: RentalPath[] = ['host-exclusive', 'host-available', 'custom-direct']
  const blockAllDirect = (reason: string) => {
    for (const path of directPaths) out[path] = reason
    return out
  }

  if (isLoading) return blockAllDirect('Checking what this provider offers…')
  if (error) return blockAllDirect('This provider is not answering right now.')
  if (!capability) return blockAllDirect('This provider has not told us what it offers.')
  if (!capability.enabled) return blockAllDirect('This provider is not renting directly at the moment.')

  // A provider on an older contract version cannot be quoted by this console:
  // the fields it signs may differ, and a quote the chain would reject is
  // worse than no quote.
  if (capability.capability_version < 1) {
    return blockAllDirect('This provider runs an older version that cannot offer fixed prices.')
  }

  // An empty list while the provider holds a lock is a reservation, not an
  // empty host — say so rather than claiming there is nothing to rent.
  if (freshOfferList.length === 0 && capability.held_locks > 0) {
    return blockAllDirect(emptyOffersCopy(capability))
  }

  // Per-product availability, from the offers themselves. A provider may serve
  // direct rental in general and have no offer for one product — plan §2
  // forbids an automatic fallback between products, so each is disabled on
  // its own evidence.
  if (offersForMode(freshOfferList, 'RENTAL_MODE_DIRECT_HOST_EXCLUSIVE').length === 0) {
    out['host-exclusive'] = 'No machine is free for exclusive use right now.'
  }
  if (offersForMode(freshOfferList, 'RENTAL_MODE_DIRECT_HOST_AVAILABLE').length === 0) {
    out['host-available'] = 'No machine has spare capacity to sell right now.'
  }
  // Custom capacity is not read from the offers: a host offer never
  // advertises it (provider `productsFor`), because the provider places a
  // custom rental on any node with room rather than on one the tenant named.
  // A capable, enabled provider is the whole of its evidence.

  return out
}

/**
 * A provider's refusal, rendered for a tenant.
 *
 * The console's own copy wins for a code it recognises, because it can say
 * more about what to do next. An unrecognised code falls back to the
 * provider's own message, which is already tenant-safe — a provider running a
 * newer contract can return a code this build has never heard of, and showing
 * a generic error there would throw away the only useful information.
 */
export function rentalErrorCopy(error: RentalErrorDto | undefined): string {
  switch (error?.code) {
    case 'rental_disabled':
      return 'This provider has stopped renting directly. Try another host, or set a maximum price and request offers.'
    case 'unknown_host_offer':
      return 'That machine is no longer available. Choose another.'
    case 'host_unavailable':
      return 'Someone else took that machine first. Choose another.'
    case 'capacity_unavailable':
      return 'That much capacity is no longer free here. Try a smaller size or another host.'
    case 'product_unavailable':
      return 'This machine cannot be rented that way right now.'
    // A product rule, not a shortage: no host can serve this, so the copy must
    // point at the paths that can rather than suggesting another machine.
    case 'persistent_storage_not_on_host_product':
      return 'Renting a whole machine cannot provide persistent storage. Rent custom capacity instead, or set a maximum price and request offers.'
    case 'quote_lifetime_invalid':
      return 'That price could not be held for long enough. Try again.'
    case 'bad_request':
      return 'This request was not valid. Check the resources and try again.'
    default:
      return error?.message || 'The provider could not give a price right now. Try again shortly.'
  }
}

// Re-exported so a component checking a quote's freshness does not need two
// imports for one question.
export { isQuoteLive }
